import React, { type JSX, useMemo, useState } from 'react';
import { useController } from 'react-hook-form';
import { MessageDescriptor } from '@lingui/core';
import { useLingui } from '@lingui/react/macro';

import { Provinces as provinces } from '@rover/constants/src/geography/canadaProvinces.constants';
import { CountryCode } from '@rover/constants/src/geography/geo.constants';
import { States as states } from '@rover/constants/src/geography/unitedStates.constants';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { DEFAULT_LOCALE } from '@rover/constants/src/i18n/locale';
import Close from '@rover/icons/svg/close.svg';
import Plus from '@rover/icons/svg/plus.svg';
import { Box, Flex, Link } from '@rover/kibble/core';
import { ConfigureFieldInterface, ZodSchemaOrEffects } from '@rover/kibble/forms';
import translateFieldError from '@rover/kibble/forms/core/translateFieldError';
import SelectField from '@rover/kibble/forms/fields/SelectField';
import TextField from '@rover/kibble/forms/fields/TextField';

import CountrySection from './CountrySection';
import PlatformLocationInput from './PlatformLocationInput';

export interface KibbleAddressFieldsProps<Z extends ZodSchemaOrEffects> {
  addressField: ConfigureFieldInterface<Z>;
  cityField: ConfigureFieldInterface<Z>;
  stateField: ConfigureFieldInterface<Z>;
  zipField: ConfigureFieldInterface<Z>;
  countryField: ConfigureFieldInterface<Z>;
  addressLine2Field?: ConfigureFieldInterface<Z>;
  countryCheckboxesField: ConfigureFieldInterface<Z>;
  showStateField?: boolean;
  locale?: Locale;
  setValue: (name: string, value: string, options?: { shouldDirty?: boolean }) => void;
  clearErrors: (name?: string) => void;
  getValues?: (name: string) => string;
  disableCountry?: boolean;
  suppressInlineAlerts?: boolean;
}

const KibbleAddressFields = <Z extends ZodSchemaOrEffects>({
  addressField,
  cityField,
  stateField,
  zipField,
  countryField,
  addressLine2Field,
  showStateField = true,
  countryCheckboxesField,
  locale = DEFAULT_LOCALE,
  setValue,
  clearErrors,
  disableCountry = false,
  getValues,
  suppressInlineAlerts = false,
}: KibbleAddressFieldsProps<Z>): JSX.Element => {
  const { t, i18n } = useLingui();
  const hasAddressLine2Value = getValues && getValues('addressLine2');
  const [isAddressLine2Expanded, setIsAddressLine2Expanded] = useState<boolean>(
    !!hasAddressLine2Value
  );

  const { field: addressFieldValue } = useController({
    control: addressField.control,
    name: addressField.name,
  });

  const { field: countryFieldValue } = useController({
    control: countryField.control,
    name: countryField.name,
  });

  const handleInputChange = (data: {
    value?: string | MessageDescriptor;
    postalCode?: string;
    countryCode?: string;
    city?: string;
    latitude?: number;
    longitude?: number;
  }): void => {
    const finalValue = String(data.value || '');
    addressFieldValue.onChange(finalValue);

    if (setValue) {
      // address format format = "123 Main St, City, State, Country"
      const parts = finalValue.split(',').map((part) => part.trim());
      const [addressLine = '', city = '', state = ''] = parts;
      setValue('zip', data.postalCode || '');
      !disableCountry && setValue('countryCode', data.countryCode || '', { shouldDirty: true });
      addressLine && setValue('addressLine1', addressLine);
      setValue('city', data.city || city);
      state && setValue('state', state);

      if (data.latitude !== undefined && data.longitude !== undefined) {
        setValue('latitude', String(data.latitude), { shouldDirty: true });
        setValue('longitude', String(data.longitude), { shouldDirty: true });
      }
      clearErrors('addressLine1');
    }
  };

  // Coordinates only ever describe the suggestion they arrived with, or the
  // stored address the form was seeded from. Nothing else here is geocoded, so
  // editing a component by hand leaves them pointing at the previous location.
  const handleAddressComponentChange = (): void => {
    if (!setValue) return;
    const hasCoordinates = getValues ? getValues('latitude') || getValues('longitude') : true;
    if (!hasCoordinates) return;

    setValue('latitude', '', { shouldDirty: true });
    setValue('longitude', '', { shouldDirty: true });
  };

  const handleRemoveAddressLine2 = (): void => {
    setIsAddressLine2Expanded(false);
    setValue && setValue('addressLine2', '');
  };

  // Get state/province configuration based on country
  const stateProvinceConfig = useMemo(() => {
    const countryCode = countryFieldValue.value || CountryCode.US;

    if (countryCode === CountryCode.CA) {
      return {
        options: provinces.map((p) => ({ value: p.code, label: p.name })),
        label: t`Province`,
        placeholder: t`Select province`,
      };
    }

    return {
      options: states.map((s) => ({ value: s.code, label: s.name })),
      label: t`State`,
      placeholder: t`Select state`,
    };
  }, [countryFieldValue.value, t]);

  return (
    <Box>
      <Flex flexDirection="column" gap="2x">
        <Box>
          <PlatformLocationInput
            inputValue={addressFieldValue.value || ''}
            onChange={handleInputChange}
            errorMessage={
              addressField.fieldError
                ? translateFieldError(addressField.fieldError, i18n)
                : undefined
            }
            setValue={setValue}
          />
        </Box>

        {addressLine2Field && (
          <Box>
            {!isAddressLine2Expanded ? (
              <Link
                onPress={() => setIsAddressLine2Expanded(true)}
                href=""
                fontWeight="semibold"
                icon={Plus}
              >
                {t`Add unit, suite, or other`}
              </Link>
            ) : (
              <Box my="2x">
                <TextField
                  {...addressLine2Field}
                  primaryLabel={t`Apartment, Unit, Suite`}
                  marginBottom="2x"
                  sensitive
                />
                <Link onPress={handleRemoveAddressLine2} href="" fontWeight="semibold" icon={Close}>
                  {t`Remove`}
                </Link>
              </Box>
            )}
          </Box>
        )}

        <Flex flexDirection={['column', 'row']} alignItems="flex-start">
          <Box mr={['0x', '4x']} width="100%">
            <TextField
              {...cityField}
              primaryLabel={t`City`}
              placeholder={t`Enter city`}
              onChange={handleAddressComponentChange}
            />
          </Box>

          <Flex flexDirection="row" gap="4x" width="100%">
            {showStateField && (
              <Box flex="1" position="relative">
                <SelectField
                  {...stateField}
                  primaryLabel={stateProvinceConfig.label}
                  options={stateProvinceConfig.options}
                  placeholder={stateProvinceConfig.placeholder}
                  onChange={handleAddressComponentChange}
                  required
                />
              </Box>
            )}
            <Box flex="1">
              <TextField
                {...zipField}
                primaryLabel={t`ZIP Code`}
                placeholder="12345"
                onChange={handleAddressComponentChange}
                sensitive
              />
            </Box>
          </Flex>
        </Flex>
        <CountrySection
          countryField={countryField}
          countryCheckboxesField={countryCheckboxesField}
          locale={locale}
          disableCountry={disableCountry}
          getValues={getValues}
          suppressInlineAlerts={suppressInlineAlerts}
          onCountryChange={handleAddressComponentChange}
        />
      </Flex>
    </Box>
  );
};

export default KibbleAddressFields;
